/**
 * Logo Cropper Component
 *
 * Allows users to crop their uploaded logo to fit common aspect ratios
 * for website headers and footers.
 */

import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { X, Check, RotateCcw } from 'lucide-react';

interface LogoCropperProps {
  imageUrl: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
  onSkip?: () => void;
}

// Aspect ratio presets for logos
const ASPECT_PRESETS = [
  { name: 'Horizontal', ratio: 3 / 1, description: 'Best for text logos' },
  { name: 'Wide', ratio: 2 / 1, description: 'Balanced layout' },
  { name: 'Square', ratio: 1 / 1, description: 'Icon or mark style' },
  { name: 'Free', ratio: undefined, description: 'Keep original shape' },
];

/**
 * Create a cropped image from the original
 */
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to the cropped area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Return as blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/png',
      1
    );
  });
}

/**
 * Create an image element from a URL
 */
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });
}

export function LogoCropper({ imageUrl, onCropComplete, onCancel, onSkip }: LogoCropperProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [selectedPreset, setSelectedPreset] = useState(3); // Default to Free (keep original shape)
  const [processing, setProcessing] = useState(false);
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);

  // "Free" must mean the image's own shape. react-easy-crop treats an
  // undefined aspect as its default of 4/3, so the preset labelled "keep
  // original shape" cropped every logo to 4:3 and cut the sides off anything
  // wider — a wide wordmark lost its last letters.
  useEffect(() => {
    let cancelled = false;
    createImage(imageUrl)
      .then((img) => {
        if (!cancelled && img.naturalHeight > 0) {
          setNaturalAspect(img.naturalWidth / img.naturalHeight);
        }
      })
      .catch(() => {
        /* the crop itself reports a load failure; fall back to square */
      });
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const onCropChange = useCallback((location: Point) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
  }, []);

  const onCropCompleteCallback = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;

    setProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);
      onCropComplete(croppedBlob);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const currentAspect = ASPECT_PRESETS[selectedPreset].ratio ?? naturalAspect ?? 1;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Crop Your Logo</h2>
            <p className="text-sm text-gray-500">Adjust the crop area to fit your logo best</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Aspect ratio presets */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex gap-2 flex-wrap">
            {ASPECT_PRESETS.map((preset, index) => (
              <button
                key={preset.name}
                onClick={() => setSelectedPreset(index)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${selectedPreset === index
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span>{preset.name}</span>
                <span className="text-xs opacity-70 ml-1">({preset.description})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Cropper area */}
        <div className="relative h-80 bg-gray-900">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={0.3}
            aspect={currentAspect}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            cropShape="rect"
            showGrid={true}
            objectFit="contain"
            style={{
              containerStyle: {
                backgroundColor: '#1f2937',
              },
            }}
          />
        </div>

        {/* Zoom slider */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 w-12">Zoom</span>
            <input
              type="range"
              min={0.3}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-primary-600"
            />
            <span className="text-sm text-gray-500 w-12 text-right">{Math.round(zoom * 100)}%</span>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {onSkip && (
              <button
                onClick={onSkip}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Skip Crop
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={processing}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {processing ? (
                <>Processing...</>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Apply Crop
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
